import { useParams, usePathname } from 'next/navigation';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { formatUrls } from '@/utils/urls/formatUrl';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Lib
import { groupHasAccess } from '@/lib/acl/pageHasAccess';

// Components
import Icon from '@/components/base/Icon';
import { CreateGroupButton } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/CreateGroupButton';
import { RegularEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/RegularEmptyState';
import { SupportedNetworkTypes } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/SupportedNetworkTypes';
import { GroupEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/GroupEmptyState';

type MultiProfilesEmptyStateProps = {
    supportedNetworkTypes: SocialProfileNetworkType[];
    emptyStateName?: string;
};

export function MultiProfilesEmptyState({
    supportedNetworkTypes,
    emptyStateName
}: MultiProfilesEmptyStateProps) {
    const pathname = usePathname();
    const params = useParams();

    const { t } = useTranslation('layout_platform');
    const groupCanAccess = formatUrls(groupHasAccess, params).includes(pathname);

    const title = emptyStateName
        ? t(`empty_states.groups.${emptyStateName}.title`)
        : t(`empty_states.groups.title`);

    const description = emptyStateName
        ? t(`empty_states.groups.${emptyStateName}.description`)
        : t(`empty_states.groups.description`);

    if (groupCanAccess) {
        return (
            <div data-testid="multi-profiles-empty-state" className="flex-1">
                <RegularEmptyState
                    tag={
                        <div className="inline-flex gap-1">
                            <Icon icon="group" />
                            {t('empty_states.multi_profiles.tag')}
                        </div>
                    }
                    title={t(title)}
                    description={t(description)}>
                    <CreateGroupButton />
                    <SupportedNetworkTypes supportedNetworkTypes={supportedNetworkTypes} />
                </RegularEmptyState>
            </div>
        );
    }

    return (
        <div data-testid="group-empty-state" className="flex-1">
            <GroupEmptyState
                supportedNetworkTypes={supportedNetworkTypes}
                emptyStateName={emptyStateName}
            />
        </div>
    );
}
